import { FC, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import {
	EvBodyDefault,
	EvFlexColumn,
	EvSection,
	EvSpinner,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import {
	MOBILE_SDK_TEST_SCANS_INCLUDE_ZERO_ISSUE_TOGGLE_DAID,
	MOBILE_SDK_TEST_SCANS_TAB_DAID,
	MOBILE_SDK_TEST_SCANS_TABLE_DAID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import {
	MobileErrorMessage,
	MobileErrorType
} from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { useMobileScansListQuery } from 'src/apps/mobile-common/hooks/useMobileScansListQuery';
import { IMobileScansListRow } from 'src/apps/mobile-common/services/mobile-scans-list';
import { EMPTY_SCANS_SUMMARY } from 'src/apps/mobile-common/services/mobile-scans-list/MobileScansList.constants';
import { MobileSdkSingleTestFiltersSection } from 'src/apps/mobile-sdk/pages/single-test/filters-section/MobileSdkSingleTestFiltersSection';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import {
	MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID,
	MOBILE_SDK_TEST_SCANS_TABLE_ID
} from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { MOBILE_SDK_TEST_SCANS_LABELS } from './MobileSdkTestScans.labels';
import { IMobileSdkTestScansProps } from './MobileSdkTestScans.types';
import { getMobileSdkTestScansColumns } from './MobileSdkTestScansTableHelper';

import './MobileSdkTestScans.scss';

export const MobileSdkTestScans: FC<IMobileSdkTestScansProps> = ({ testId }) => {
	const history = useHistory();
	const { getTableStateById, onFilterChange } = useTableState();

	const tableId = useMemo(() => buildFullTableId(MOBILE_SDK_TEST_SCANS_TABLE_ID, testId), [testId]);
	const issuesTableId = useMemo(
		() => buildFullTableId(MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID, testId),
		[testId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const scansQuery = useMobileScansListQuery({
		runId: testId,
		productName: ProductType.MOBILE_SDK,
		includeScansWithoutIssues: filters.includeScansWithoutIssues ?? false,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filterOptions: filters
	});

	const handleScanNameClick = useCallback(
		(scan: IMobileScansListRow): void => {
			onFilterChange(issuesTableId, {
				scans: [{ value: scan.scanId, label: scan.scanName }]
			});
		},
		[issuesTableId, onFilterChange]
	);

	const columns = useMemo(
		() => getMobileSdkTestScansColumns(handleScanNameClick, history.location?.pathname),
		[handleScanNameClick, history.location?.pathname]
	);

	if (scansQuery.isError) {
		return (
			<MobileErrorMessage
				errorType={MobileErrorType.LOADING_ERROR}
				productType={ProductType.MOBILE_SDK}
			/>
		);
	}

	if (scansQuery.isLoading) {
		return <EvSpinner />;
	}

	const { rows = [], total = 0, summary = EMPTY_SCANS_SUMMARY } = scansQuery.data ?? {};
	const isTableLoading = scansQuery.isPreviousData;

	return (
		<EvSection
			dataAutomationId={MOBILE_SDK_TEST_SCANS_TAB_DAID}
			ariaLabel={MOBILE_SDK_TEST_SCANS_LABELS.SECTION_ARIA_LABEL}
		>
			<EvFlexColumn padding={['MEDIUM', 'NONE']}>
				<MobileSdkSingleTestFiltersSection
					testId={testId}
					filters={filters}
					ariaLabel={MOBILE_SDK_TEST_SCANS_LABELS.FILTERS_ARIA_LABEL}
					onFilterChange={onFilterChange}
					tableId={tableId}
					showIncludeZeroIssueToggle
					includeZeroIssueToggleDataAutomationId={
						MOBILE_SDK_TEST_SCANS_INCLUDE_ZERO_ISSUE_TOGGLE_DAID
					}
				/>
			</EvFlexColumn>
			<EvSection
				ariaLabel={MOBILE_SDK_TEST_SCANS_LABELS.TABLE_ARIA_LABEL}
				skipLinkId="main-section"
			>
				<PlatformTable
					dataAutomationId={MOBILE_SDK_TEST_SCANS_TABLE_DAID}
					isTableLoading={isTableLoading}
					tableId={tableId}
					tableData={rows}
					totalCount={total}
					totalPaginationableResults={total}
					className="mobile-sdk-test-scans__table"
					persistTableState
					columns={columns}
					topRowActions={
						<EvBodyDefault color={TEXT_COLOR.CONTENT_3}>
							{MOBILE_SDK_TEST_SCANS_LABELS.ISSUES_ELEMENTS_SUMMARY(summary)}
						</EvBodyDefault>
					}
					alignActionsRight={false}
					options={{
						keyField: 'scanId',
						title: MOBILE_SDK_TEST_SCANS_LABELS.TABLE_TITLE_WITH_SUMMARY(summary),
						caption: MOBILE_SDK_TEST_SCANS_LABELS.TABLE_CAPTION,
						pagination: true,
						dataType: 'scan',
						remote: true
					}}
					withUrlSwitcher={false}
				/>
			</EvSection>
		</EvSection>
	);
};
